import React, { useEffect, useMemo, useState } from 'react';
import { ClickableField, DialogSection, Nullable } from '@linetweet/linetweet-ui';
import { CircularProgress, FormHelperText, Stack, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import clsx from 'clsx';
import { Alert } from '@mui/lab';

import { Spin } from 'features/commons';
import { SlotButtonGroup } from '../subcomponents';
import { AppointmentFormSlotItem } from '../types';

import styles from './SlotSelectSection.module.scss';

type Props = {
  value: Nullable<AppointmentFormSlotItem>;
  onChange: (value: Nullable<AppointmentFormSlotItem>) => void;
  options: AppointmentFormSlotItem[];
  loading?: boolean;
  employeeId?: string;
  swapEnabled?: boolean;
  swapOptions?: AppointmentFormSlotItem[];
  onFetchSwapOptionsClick?: () => void;
  swapOptionsLoading?: boolean;
  swapOptionsError?: boolean;
};

type SwapSlotInputProps = {
  value: Nullable<AppointmentFormSlotItem>;
  onChange: (value: Nullable<AppointmentFormSlotItem>) => void;
  employeeId?: string;
  options?: AppointmentFormSlotItem[];
  onFetchOptionsClick?: () => void;
  optionsLoading?: boolean;
  optionsError?: boolean;
  isDisabled?: boolean;
};

function SwapSlotInput({
  value,
  onChange,
  employeeId,
  options,
  onFetchOptionsClick,
  optionsLoading,
  optionsError,
  isDisabled,
}: SwapSlotInputProps) {
  const intl = useIntl();

  return (
    <>
      {employeeId && (
        <>
          {((!options && !optionsError) || optionsLoading) && (
            <Stack direction="row" spacing={1} alignItems="center">
              { optionsLoading && (
                <CircularProgress size="16px" />
              ) }

              <div
                className={clsx(styles.swapTimesFetchButton, {
                  [styles.swapTimesFetchButtonDisabled]: optionsLoading || isDisabled,
                })}
              >
                <ClickableField
                  disabled={optionsLoading || isDisabled}
                  onClick={onFetchOptionsClick}
                  label={intl.formatMessage({ id: 'appointment.swapTimesFetch' })}
                />
              </div>

              <Tooltip
                slotProps={{
                  tooltip: {
                    className: styles.swapTooltip,
                  },
                }}
                title={(
                  <Alert
                    sx={{ whiteSpace: 'pre-wrap' }}
                    icon={false}
                    severity="error"
                    className={styles.swapAlert}
                  >
                    { intl.formatMessage({ id: 'appointment.swapTimesDescription' }) }
                  </Alert>
                )}
              >
                <InfoOutlinedIcon className={styles.swapInfoIcon} />
              </Tooltip>
            </Stack>
          )}

          {!!(!optionsLoading && (options || optionsError)) && (
            <DialogSection title={intl.formatMessage({ id: 'appointment.swapTimes' })}>
              {!!(options && options.length) && (
                <SlotButtonGroup
                  dataTestId="slot-selection-swap-times-list"
                  value={value}
                  onChange={onChange}
                  options={options}
                  isDisabled={isDisabled}
                />
              )}

              {!!((options && !options.length) || optionsError) && (
                <FormHelperText>
                  { intl.formatMessage({ id: 'appointment.swapTimesEmpty' }) }
                </FormHelperText>
              )}
            </DialogSection>
          )}
        </>
      )}

      {!employeeId && (
        <Alert severity="error" className={styles.swapAlert}>
          { intl.formatMessage({ id: 'appointment.swapTimesEmployeeNotSelected' }) }
        </Alert>
      )}
    </>
  );
}

SwapSlotInput.defaultProps = {
  employeeId: undefined,
  options: undefined,
  onFetchOptionsClick: undefined,
  optionsLoading: undefined,
  optionsError: undefined,
  isDisabled: undefined,
};

function PreferredSlotSelectSectionComponent({
  value,
  onChange,
  options: propsOptions,
  loading,
  employeeId,
  swapEnabled,
  swapOptions,
  onFetchSwapOptionsClick,
  swapOptionsLoading,
  swapOptionsError,
}: Props) {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const plainSlotValue = useMemo(() => (value && !value.extra?.isSwap ? value : null), [value]);

  const swapSlotValue = useMemo(() => (value && value.extra?.isSwap ? value : null), [value]);

  const options = useMemo(() => {
    const hasValueInTheList: boolean = !!propsOptions.find((option) => option.value === plainSlotValue?.value);

    if (plainSlotValue?.value && !hasValueInTheList) {
      return [plainSlotValue, ...propsOptions];
    }

    return propsOptions;
  }, [propsOptions, plainSlotValue]);

  const preferredOptions = useMemo(() => options.slice(0, 3), [options]);

  useEffect(() => {
    if (!isExpanded && plainSlotValue?.value && !preferredOptions.find((option) => option.value === plainSlotValue.value)) {
      setIsExpanded(true);
    }
  }, [preferredOptions, plainSlotValue]);

  const shouldShowExpandedSlots = options.length > 3;

  return (
    <Spin loading={loading}>
      <Stack direction="column" spacing={2.5}>
        <DialogSection title={intl.formatMessage({ id: 'appointment.preferredTimes' })}>
          <SlotButtonGroup
            dataTestId="slot-selection-preferred-times-list"
            value={plainSlotValue}
            onChange={onChange}
            options={preferredOptions}
          />
        </DialogSection>

        {isExpanded && (
          <>
            {shouldShowExpandedSlots && (
              <DialogSection title={intl.formatMessage({ id: 'appointment.availableTimes' })}>
                <SlotButtonGroup
                  dataTestId="slot-selection-available-times-list"
                  value={plainSlotValue}
                  onChange={onChange}
                  options={options}
                />
              </DialogSection>
            )}

            {swapEnabled && (
              <SwapSlotInput
                value={swapSlotValue}
                onChange={onChange}
                employeeId={employeeId}
                options={swapOptions}
                onFetchOptionsClick={onFetchSwapOptionsClick}
                optionsLoading={swapOptionsLoading}
                optionsError={swapOptionsError}
              />
            )}
          </>
        )}

        {!!(swapEnabled || options.length > 3) && (
          <ClickableField
            data-testid="slot-select-expand-button"
            disabled={loading}
            onClick={() => setIsExpanded(!isExpanded)}
            label={intl.formatMessage({ id: isExpanded ? 'appointment.showPreferred' : 'appointment.showAll' })}
          />
        )}
      </Stack>
    </Spin>
  );
}

PreferredSlotSelectSectionComponent.defaultProps = {
  loading: false,
  employeeId: undefined,
  swapEnabled: false,
  swapOptions: undefined,
  onFetchSwapOptionsClick: undefined,
  swapOptionsLoading: false,
  swapOptionsError: false,
};

export const PreferredSlotSelectSection = React.memo(PreferredSlotSelectSectionComponent);
